/* eslint-disable max-lines */

import {getLocalStorageItem, setLocalStorageItem} from '../../utils/localStorage';
import {makeStyles} from '@material-ui/styles';
import {omit, every, throttle, some, compact, includes} from 'lodash';
import classNames from 'classnames';
import Mapbox, {MapboxProps} from '../../utils/mapbox/Mapbox';
import mapboxgl from 'mapbox-gl';
import React, {Profiler, useState, useCallback, useEffect, useMemo, useRef} from 'react';
import theme from '../../theme';
import useInterval from '../../utils/react/useInterval';
import useWaypoints from '../../hooks/useWaypoints';
import useTracks from '../../hooks/useTracks';
import useAreas from '../../hooks/useAreas';
import useLayers from '../../hooks/useLayers';
import useOverlaysState from '../../hooks/useOverlaysState';
import useControllableState from '../../hooks/useControllableState';
import getMapboxImages from '../../utils/gaia/getMapboxImages';
import WaypointSnackbar from './snackbars/WaypointSnackbar';
import TrackSnackbar from './snackbars/TrackSnackbar';
import HighlightSnackbar from './snackbars/HighlightSnackbar';
import getInteractionHandlers from '../../utils/gaia/getInteractionHandlers';
import PrintPreview from './PrintPreview';
import addSafariPinchZoom from '../../utils/mapbox/addSafariPinchZoom';
import trackAnalytics from '../../utils/gaia/trackAnalytics';
import useBeforeUnloadConfirmation from '../../utils/react/useBeforeUnloadConfirmation';
import getIsMapboxGLSupported from '@mapbox/mapbox-gl-supported';
import {parseUrlProps, stringifyUrlProps} from './urlProps';
import useGetSyncPromise from '../../utils/react/useGetSyncPromise';
import {controlsZIndex} from '../../utils/gaia/zIndicies';
import getWebGLRendererInfo from '../../utils/getWebGLRendererInfo';
import getPopup from './popups/getPopup';
import getSyncMapboxStyle from '../../utils/gaia/getSyncMapboxStyle';
import SidebarManager from './sidebars/SidebarManager';
import {sidebarWidth} from './sidebars/Sidebar';
import fetchUser from '../../utils/gaia/fetchUser';
import trackLayerAnalytics from '../../utils/gaia/trackLayerAnalytics';
import getControls from './controls/getControls';

// eslint-disable-next-line max-len
mapboxgl.accessToken = 'pk.eyJ1IjoidHJhaWxiZWhpbmQiLCJhIjoiY2psaWFsNmx2MGNvNTN2b3gyYjgwNDQ3ciJ9.Rymwleik2AUWDg8NS0-scg';

const isMapboxGLSupported = getIsMapboxGLSupported();
if (!isMapboxGLSupported) {
    trackAnalytics('Mapbox GL Not Supported', {
        ...getWebGLRendererInfo(),
        category: 'map2'
    });
}

// Safari errors if window.history.replaceState is called more than 100 times in 30 seconds.
export const throttledHistoryReplaceState = throttle(
    window.history.replaceState.bind(window.history),
    30000 / 100 + 1,
    {leading: true, trailing: true},
);

export interface MapProps extends Partial<MapboxProps> {
    preview?: boolean;
    highlightLocation?: mapboxgl.LngLatLike;
    layers?: Layer[];
    legacyPrint?: boolean;
    grid?: Grid;
    savePosition?: boolean;
    showGeolocateControl?: boolean;
    showPrintControl?: boolean;
    showFullscreenControl?: boolean;
    showSearchBoxControl?: boolean;
    highlight?: MapHighlight;
    showDrawControl?: boolean;
    onMapboxStyleChange?: (event: {target: mapboxgl.Map}) => void;
    showWaypoints?: boolean;
    showWaypointsReason?: string;
    showTracks?: boolean;
    showTracksReason?: string;
    showAreas?: boolean;
    showAreasReason?: string;
}

export default function Map(props: MapProps) {
    const classes = useStyles(); // eslint-disable-line no-use-before-define
    const getSyncPromise = useGetSyncPromise();

    const [user] = getSyncPromise(fetchUser());
    const isLayered: boolean | undefined = (
        user &&
        user.hasPremiumSubscription &&
        !includes(navigator.userAgent.toLowerCase(), 'mobile')
    );

    const urlProps = parseUrlProps(location.search);

    const [interaction, onInteractionChange] = useState<MapInteraction>({
        type: 'normal',
        subtype: 'normal'
    });

    const [overlays, onOverlaysChange] = useOverlaysState({
        showTracksProp: props.showTracks,
        showWaypointsProp: props.showWaypoints,
        showAreasProp: props.showAreas,
    });

    const [bounds, onBoundsChange] = useState<mapboxgl.LngLatBounds>();
    const [foldersMembership, onFoldersMembershipChange] = useState<MapFoldersMembership>({});

    const [grid, onGridChange] = useControllableState<Grid>(
        getLocalStorageItem('grid', 'none'),
        props.grid
    );

    const [highlight, onHighlightChange] = useControllableState<MapHighlight | undefined>(
        urlProps.highlight,
        props.highlight
    );

    const [isMapLoaded, onIsMapLoadedChange] = useState<boolean>();
    const [map, onMapChange] = useState<mapboxgl.Map>();
    const [popup, onPopupChange] = useState<MapPopup | undefined>(urlProps.popup);
    const [searchResult, onSearchResultChange] = useState<SearchResult>();
    const [sidebar, onSidebarChange] = useState<MapSidebar>(urlProps.sidebar || {type: 'hidden'});
    const [zoom, onZoomChange] = useState<number>();

    const [highlightLocation, onHighlightLocationChange] = useControllableState<
        mapboxgl.LngLat | undefined
    >(
        undefined,
        props.highlightLocation && mapboxgl.LngLat.convert(props.highlightLocation)
    );

    const project = useMemo(() => map && map.project.bind(map), [map]);

    const {onMapboxStyleChange} = props;
    const [tracks, trackDispatcher, isTracksSaved] = useTracks(project, highlight);
    const [waypoints, waypointDispatcher, isWaypointsSaved] = useWaypoints(tracks, trackDispatcher);
    const [areas, areaDispatcher, isAreasSaved] = useAreas(highlight);
    const [
        {layers, userLayers, layerTypes},
        userLayerDispatcher,
        isUserLayersSaved
    ] = useLayers(props.layers, urlProps.layers, {isLayered});

    const [{mapboxStyle, transformRequest}, , isMapboxStyleLoaded] = getSyncMapboxStyle({
        areas,
        bounds,
        foldersMembership,
        getSyncPromise,
        grid: props.layers ? 'none' : grid,
        highlight,
        highlightLocation,
        interaction,
        isPrinting: sidebar.type === 'print',
        layers,
        layerTypes,
        preview: props.preview,
        project,
        searchResult: searchResult,
        showAreas: !props.layers && overlays.showAreas,
        showPublicTracks: !props.layers && overlays.showPublicTracks,
        showTracks: !props.layers && overlays.showTracks,
        showRoutes: !props.layers && overlays.showRoutes,
        showWaypointLabels: overlays.showWaypointLabels,
        showWaypoints: !props.layers && overlays.showWaypoints,
        tracks,
        waypoints,
        zoom,
    });

    useBeforeUnloadConfirmation(
        interaction.type === 'movingWaypoint' ||
        interaction.type === 'editingRoute'
    );

    useEffect(() => {
        if (grid !== 'none') {
            trackLayerAnalytics('Add Grid Overlay', userLayers, {
                gridName: grid,
                category: 'map2',
            });
        }
        setLocalStorageItem('grid', grid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grid]);

    useEffect(() => {
        if (user && !user.hasSubscription && sidebar.type === 'print') {
            onSidebarChange({type: 'hidden'});
        }
    }, [user, sidebar]);

    const onProfilerRenderRef = useRef<React.ProfilerOnRenderCallback>(() => {});

    useEffect(() => {
        if (!map || !searchResult) return;
        if (searchResult.bounds) {
            map.fitBounds(searchResult.bounds, {maxZoom: 16, duration: 750});
        } else {
            map.flyTo({center: searchResult.centroid, zoom: 16, duration: 750});
        }
    }, [map, searchResult]);

    const resizeMap = useCallback(() => {
        if (!map) return;
        map.resize();
    }, [map]);

    useEffect(() => {
        if (onMapboxStyleChange && mapboxStyle && map && isMapboxStyleLoaded) {
            map.once('styledata', () => onMapboxStyleChange({target: map}));
        }
    }, [isMapboxStyleLoaded, map, mapboxStyle, onMapboxStyleChange]);

    useEffect(() => {
        if (sidebar.type === 'print') {
            resizeMap();
        }
    }, [resizeMap, sidebar]);

    let initialCenter: mapboxgl.LngLatLike | undefined;
    if (bounds) {
        // this handles the case that <Mapbox /> gets unmounted / remounted
        initialCenter = bounds.getCenter();
    } else if (!props.savePosition) {
        initialCenter = undefined;
    } else if (urlProps.center) {
        initialCenter = urlProps.center;
    } else {
        initialCenter = getLocalStorageItem('center', undefined);
    }

    let initialZoom: number | undefined;
    if (zoom) initialZoom = zoom;
    else if (!props.savePosition) initialZoom = undefined;
    else if (urlProps.zoom) initialZoom = Number(urlProps.zoom);
    else initialZoom = getLocalStorageItem('zoom', undefined);

    useInterval(() => {
        onIsMapLoadedChange(
            map &&
            mapboxStyle &&
            every(mapboxStyle.sources, (source: mapboxgl.Source, id: string) => {
                return source.type === 'geojson' || (map.getSource(id) && map.isSourceLoaded(id));
            })
        );
    }, 250);

    useEffect(() => {
        if (!layers) return;

        const nextSearch = stringifyUrlProps({
            ...props.savePosition && bounds && {center: bounds.getCenter(), zoom},
            ...!props.layers && {layers: layers.map(layer => layer.type)},
            ...location.pathname === '/map/' && {highlight},
            sidebar: sidebar,
            popup,
        });

        if (nextSearch !== location.search) {
            throttledHistoryReplaceState(
                null,
                '',
                location.origin + location.pathname + nextSearch
            );
        }

        if (props.savePosition) {
            setLocalStorageItem('center', bounds && bounds.getCenter());
            setLocalStorageItem('zoom', zoom);
        }

    }, [bounds, highlight, layers, popup, props.layers, props.savePosition, sidebar, zoom]);

    function mapboxRef(nextMap: mapboxgl.Map) {
        if (props.mapboxRef) props.mapboxRef(nextMap);
        onMapChange(nextMap);
        onZoomChange(nextMap.getZoom());
        onBoundsChange(nextMap.getBounds());
        nextMap.dragRotate.disable();
        nextMap.touchZoomRotate.disableRotation();
        addSafariPinchZoom(nextMap);
    }

    const onWaypointMoveStart = useCallback((
        waypoint: Waypoint,
        mapboxLayerId: string | undefined
    ) => {
        onInteractionChange({
            type: 'movingWaypoint',
            pointLocalId: waypoint.properties.pointLocalId,
            subtype: 'normal',
            initialWaypoint: waypoint,
            mapboxLayerId
        });
        onPopupChange(undefined);
    }, []);

    const onTrackMoveStart = useCallback((
        trackLocalId: string, mapboxLayerId:
        string | undefined
    ) => {
        trackLayerAnalytics('Edit Route', userLayers, {category: 'map2'});
        const track = tracks[trackLocalId];
        if (!track.isGeometryLoaded) return;
        if (some(track.points, point => point.properties.attr === null)) {
            onOverlaysChange({
                ...overlays,
                showWaypoints: true
            });
        }
        onInteractionChange({
            type: 'editingRoute',
            trackLocalId: track.properties.trackLocalId,
            subtype: 'appendingPoint',
            initialTrack: track,
            cursorLngLat: undefined,
            idle: false,
            mapboxLayerId
        });
        onPopupChange(undefined);
    }, [userLayers, tracks, onOverlaysChange, overlays]);

    const {onMove} = props;
    const onMapboxMove = useCallback((event: mapboxgl.MapboxEvent) => {
        if (onMove) onMove(event);
        onZoomChange(map!.getZoom());
        onBoundsChange(map!.getBounds());
    }, [onMove, map]);

    const className = classNames({
        [classes.hideSidebar]: true,
        [classes.showSidebar]: sidebar.type !== 'hidden',
        [classes.mapContainer]: true,
    });

    const {cursor, ...interactionHandlers} = getInteractionHandlers({
        interaction,
        mapboxStyle: mapboxStyle,
        onClickExternal: props.onClick,
        onInteractionChange,
        onMouseDownExternal: props.onMouseDown,
        onMouseMoveExternal: props.onMouseMove,
        onMouseUpExternal: props.onMouseUp,
        onPopupChange,
        popup,
        onTouchEndExternal: props.onTouchEnd,
        onTouchMoveExternal: props.onTouchMove,
        onTouchStartExternal: props.onTouchStart,
        trackDispatcher,
        tracks,
        waypointDispatcher,
        waypoints,
        areaDispatcher,
        areas,
        onSearchResultChange,
        highlight,
        onFoldersMembershipChange,
        foldersMembership,
        map,
        onOverlaysChange,
        overlays,
    });

    useEffect(() => {
        if (map) map.getCanvas().style.cursor = cursor;
    }, [cursor, map]);

    if (!isMapboxGLSupported) {
        return <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
        }}>
            <strong style={{color: theme.palette.secondary.main, marginBottom: theme.spacing(1)}}>
                Sorry! Your computer cannot display this map.
            </strong>
            <div style={{marginBottom: theme.spacing(2)}}>
                Please try downloading the Gaia GPS mobile app.
            </div>
            <div>
                <a
                    href={'https://itunes.apple.com/us/app/gaia-gps/id1201979492?mt=8&uo=4'}
                    target="itunes_store"
                >
                    <img
                        width="135"
                        src={`${Gaia.staticURL}img/appstore.svg`}
                        alt="Download on the App Store"
                        style={{marginRight: theme.spacing(1)}}
                    />
                </a>
                <a
                    // eslint-disable-next-line max-len
                    href="https://market.android.com/details?id=com.trailbehind.android.gaiagps.pro&hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                        width="135"
                        src={`${Gaia.staticURL}img/icons/play_badge.png`}
                        alt="Get it on Google Play"
                    />
                </a>
            </div>
        </div>;

    } else {
        return <Profiler id="Map" onRender={onProfilerRenderRef.current}><div
            className={classes.root}
            style={{
                ...props.style,
                transition: `border-width ${theme.transitions.duration.standard}ms`,
                borderColor: theme.palette.primary.main,
                borderStyle: 'solid',
                boxSizing: 'border-box',
                borderWidth: sidebar.type !== 'print' && interaction.type !== 'normal' ? 6 : 0
            }}
        >
            <div style={{height: '100%'}} className={className}>
                <PrintPreview
                    sidebar={sidebar}
                    center={bounds ? bounds.getCenter() : undefined}
                    zoom={zoom}
                    layers={userLayers ?
                        userLayers
                            .filter((userLayer: UserLayer) => userLayer.isVisible)
                            .map((userLayer: UserLayer) => userLayer.layer) :
                        undefined
                    }
                    layerTypes={layerTypes}
                    map={map}
                >
                    <Mapbox
                        {...omit(props, 'style', 'classes')}
                        className={classes.map}
                        mapboxStyle={mapboxStyle}
                        images={getSyncPromise(getMapboxImages(), {})[0]}
                        options={{
                            pitchWithRotate: false,
                            refreshExpiredTiles: false,
                            ...initialCenter ? {center: initialCenter} : undefined,
                            ...initialZoom ? {zoom: initialZoom} : undefined,
                            ...props.options,
                            attributionControl: sidebar.type !== 'print',
                        }}
                        onError={({error}: mapboxgl.ErrorEvent) => {
                        // HTTP errors already get printed to the console.
                            if ((error as any).status) return;
                            else if (
                                error.message === 'http status 200 returned without content.'
                            ) return;
                            else if (error.message === 'cancelled') return;
                            else throw error;
                        }}
                        onMove={onMapboxMove}
                        mapboxRef={mapboxRef}
                        controls={getControls({
                            bounds,
                            highlight,
                            interaction,
                            loadingMessages: compact([
                                isUserLayersSaved ? undefined : 'layer metadata',
                                isMapboxStyleLoaded ? undefined : 'Mapbox style',
                                isMapLoaded ? undefined : 'map',
                                isTracksSaved ? undefined : 'tracks',
                                isWaypointsSaved ? undefined : 'waypoints',
                                isAreasSaved ? undefined : 'areas',
                            ]),
                            map,
                            onInteractionChange,
                            onOverlaysChange,
                            onPopupChange,
                            onSearchResultChange,
                            onSidebarChange,
                            overlays,
                            showDrawControl: Boolean(props.showDrawControl),
                            showFullscreenControl: Boolean(props.showFullscreenControl),
                            showGeolocateControl: props.showGeolocateControl !== false,
                            showLayersControl: !props.layers,
                            showPrintControl: Boolean(props.showPrintControl),
                            showSearchBoxControl: Boolean(props.showSearchBoxControl),
                            sidebar,
                            trackDispatcher,
                            userLayers,
                            waypointDispatcher,
                            zoom,
                            user,
                        })}
                        popup={getPopup({
                            popup,
                            tracks,
                            waypoints,
                            areas,
                            waypointDispatcher,
                            trackDispatcher,
                            areaDispatcher,
                            onPopupChange,
                            onOverlaysChange,
                            overlays,
                            onWaypointMoveStart,
                            onTrackMoveStart,
                            bounds,
                            project,
                            interaction,
                            mapboxStyle,
                            getSyncPromise,
                            className: classes.popup,
                        })}
                        {...interactionHandlers}
                        transformRequest={transformRequest}
                    />
                </PrintPreview>

                <SidebarManager
                    className={classes.sidebar}
                    showGridSelect={!props.grid}
                    onOverlaysChange={onOverlaysChange}
                    onGridChange={onGridChange}
                    grid={grid}
                    overlays={overlays}
                    onSidebarChange={onSidebarChange}
                    interactionType={interaction.type}
                    userLayers={userLayers}
                    userLayerDispatcher={userLayerDispatcher}
                    highlight={highlight}
                    layerTypes={layerTypes}
                    disableShowWaypoints={props.showWaypoints !== undefined}
                    disableShowWaypointsReason={props.showWaypointsReason}
                    disableShowTracks={props.showTracks !== undefined}
                    disableShowTracksReason={props.showTracksReason}
                    disableShowAreas={props.showAreas !== undefined}
                    disableShowAreasReason={props.showAreasReason}
                    sidebar={sidebar}
                    map={map}
                    layers={layers}
                    onProfilerRenderRef={onProfilerRenderRef}
                    mapboxStyle={mapboxStyle}
                    waypointDispatcher={waypointDispatcher}
                    trackDispatcher={trackDispatcher}
                    waypoints={waypoints}
                    interaction={interaction}
                    onInteractionChange={onInteractionChange}
                    onEntered={resizeMap}
                    onExited={resizeMap}
                    isLayered={isLayered}
                    onPopupChange={onPopupChange}
                    popup={popup}
                    project={project}
                    tracks={tracks}
                />

                {interaction.type === 'movingWaypoint' && <WaypointSnackbar
                    interaction={interaction}
                    onInteractionChange={onInteractionChange}
                    waypoints={waypoints}
                    waypointDispatcher={waypointDispatcher}
                    map={map!}
                    onPopupChange={onPopupChange}
                />}

                {interaction.type === 'editingRoute' && <TrackSnackbar
                    interaction={interaction}
                    onInteractionChange={onInteractionChange}
                    tracks={tracks}
                    trackDispatcher={trackDispatcher}
                    project={project}
                    onPopupChange={onPopupChange}
                    onHighlightLocationChange={onHighlightLocationChange}
                />}

                <HighlightSnackbar
                    isHighlightControlled={Boolean(props.highlight)}
                    interaction={interaction}
                    sidebar={sidebar}
                    highlight={highlight}
                    onHighlightChange={onHighlightChange}
                />
            </div>
        </div></Profiler>;
    }
}

const useStyles = makeStyles({
    popup: {
        '& .mapboxgl-popup-content': {
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(2),
            boxShadow: theme.shadows[13],
        }
    },
    root: {
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        '& .mapboxgl-ctrl-top-left': {
            zIndex: controlsZIndex,
        },
        '& .mapboxgl-ctrl button[disabled]': {
            color: '#aaa',
            cursor: 'default',
        }
    },
    map: {
        height: '100%',
        '& .mapboxgl-canvas-container': {
            '@media print': {
                display: 'none'
            }
        }
    },
    mapContainer: {
        '@media print': {
            margin: '0 !important',
            padding: 0,
        }
    },
    sidebar: {
        width: sidebarWidth,
        top: 0,
        bottom: 0,
        left: 0,
        position: 'absolute'
    },

    // These transitions are intended to mirror the Slide component
    // https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/Slide/Slide.js
    showSidebar: {
        marginLeft: sidebarWidth,
        transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        })
    },
    hideSidebar: {
        transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        })
    },
});
